/**
 * Sticky Add to Cart Button
 * Shows a fixed bottom bar with product info and ATC when user scrolls past the original button
 */

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', class StickyATC extends HTMLElement {
    constructor() {
      super();

      this.productForm = document.querySelector('product-form');
      this.originalButton = document.querySelector('#ProductSubmitButton-' + this.dataset.sectionId);
      this.stickyButton = this.querySelector('.sticky-atc__button');
      this.quantityInput = this.querySelector('.sticky-atc__quantity-input');
      this.decreaseBtn = this.querySelector('[data-action="decrease"]');
      this.increaseBtn = this.querySelector('[data-action="increase"]');

      // Intersection Observer to show/hide sticky button
      this.setupObserver();

      // Sync quantity with main form
      this.setupQuantitySync();

      // Handle sticky button click
      this.setupButtonClick();

      // Handle variant changes
      this.setupVariantListener();
    }

    setupObserver() {
      if (!this.originalButton) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            // Show sticky bar when original button is not visible
            if (entry.isIntersecting) {
              this.classList.remove('visible');
            } else {
              // Only show if user has scrolled past the button (not before)
              const rect = this.originalButton.getBoundingClientRect();
              if (rect.top < 0) {
                this.classList.add('visible');
              }
            }
          });
        },
        {
          threshold: 0,
          rootMargin: '0px'
        }
      );

      observer.observe(this.originalButton);
    }

    setupQuantitySync() {
      if (!this.quantityInput) return;

      // Sync with main quantity selector
      const mainQuantityInput = document.querySelector('#Quantity-Form-' + this.dataset.sectionId + ' input[name="quantity"]');

      if (mainQuantityInput) {
        // Initial sync
        this.quantityInput.value = mainQuantityInput.value || 1;

        // Listen to main quantity changes
        mainQuantityInput.addEventListener('change', () => {
          this.quantityInput.value = mainQuantityInput.value;
          this.updateQuantityButtons();
        });
      }

      // Decrease button
      if (this.decreaseBtn) {
        this.decreaseBtn.addEventListener('click', () => {
          const currentValue = parseInt(this.quantityInput.value) || 1;
          if (currentValue > 1) {
            this.quantityInput.value = currentValue - 1;
            this.syncMainQuantity();
            this.updateQuantityButtons();
          }
        });
      }

      // Increase button
      if (this.increaseBtn) {
        this.increaseBtn.addEventListener('click', () => {
          const currentValue = parseInt(this.quantityInput.value) || 1;
          const maxValue = parseInt(this.quantityInput.getAttribute('max')) || 999;
          if (currentValue < maxValue) {
            this.quantityInput.value = currentValue + 1;
            this.syncMainQuantity();
            this.updateQuantityButtons();
          }
        });
      }

      // Direct input change
      this.quantityInput.addEventListener('change', () => {
        this.syncMainQuantity();
        this.updateQuantityButtons();
      });

      this.updateQuantityButtons();
    }

    syncMainQuantity() {
      const mainQuantityInput = document.querySelector('#Quantity-Form-' + this.dataset.sectionId + ' input[name="quantity"]');
      if (mainQuantityInput) {
        mainQuantityInput.value = this.quantityInput.value;
        mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateQuantityButtons() {
      const currentValue = parseInt(this.quantityInput.value) || 1;
      const maxValue = parseInt(this.quantityInput.getAttribute('max')) || 999;

      if (this.decreaseBtn) {
        this.decreaseBtn.disabled = currentValue <= 1;
      }

      if (this.increaseBtn) {
        this.increaseBtn.disabled = currentValue >= maxValue;
      }
    }

    setupButtonClick() {
      if (!this.stickyButton || !this.originalButton) return;

      this.stickyButton.addEventListener('click', (e) => {
        e.preventDefault();

        // Sync quantity before submitting
        this.syncMainQuantity();

        // Add loading state
        this.stickyButton.classList.add('adding');
        this.stickyButton.disabled = true;

        // Trigger the original form submission
        const form = this.productForm.querySelector('form');
        if (form) {
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          form.dispatchEvent(submitEvent);
        }

        // Remove loading state after a delay
        setTimeout(() => {
          this.stickyButton.classList.remove('adding');
          this.stickyButton.disabled = false;
        }, 1500);
      });
    }

    setupVariantListener() {
      // Listen for variant changes to update price and availability
      document.addEventListener('variant:change', (e) => {
        const variant = e.detail?.variant;
        if (!variant) return;

        this.updatePrice(variant);
        this.updateAvailability(variant);
      });

      // Also listen to the variant input change
      const variantInput = document.querySelector('.product-variant-id');
      if (variantInput) {
        const observer = new MutationObserver(() => {
          const variantId = variantInput.value;
          // Get variant data from window if available
          if (window.productVariants) {
            const variant = window.productVariants.find(v => v.id == variantId);
            if (variant) {
              this.updatePrice(variant);
              this.updateAvailability(variant);
            }
          }
        });

        observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
      }
    }

    updatePrice(variant) {
      const priceElement = this.querySelector('.sticky-atc__price');
      const comparePriceElement = this.querySelector('.sticky-atc__price--compare');

      if (!priceElement) return;

      if (variant.price) {
        priceElement.textContent = this.formatMoney(variant.price);
      }

      if (comparePriceElement) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceElement.textContent = this.formatMoney(variant.compare_at_price);
          comparePriceElement.style.display = 'inline';
        } else {
          comparePriceElement.style.display = 'none';
        }
      }
    }

    updateAvailability(variant) {
      const buttonText = this.stickyButton.querySelector('span');

      if (!variant.available) {
        this.stickyButton.disabled = true;
        if (buttonText) {
          buttonText.textContent = this.dataset.soldOutText || 'Sold out';
        }
      } else {
        this.stickyButton.disabled = false;
        if (buttonText) {
          buttonText.textContent = this.dataset.buttonText || 'Add to cart';
        }
      }
    }

    formatMoney(cents) {
      // Basic money formatting - adjust based on your shop's currency
      const amount = (cents / 100).toFixed(2);
      return this.dataset.moneyFormat
        ? this.dataset.moneyFormat.replace('{{amount}}', amount)
        : `$${amount}`;
    }
  });
}
